import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getCartItemCount, getWishlistItemCount } from '../../utils/cartUtils';
import { useAuth } from '../../contexts/AuthContext';
import { mainServices, subServicesData } from '../../data/servicesData';
import LanguageSwitcher from '../LanguageSwitcher';
import './Header.css';

// Simple debounce function
const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// Local search function for Header component (doesn't affect global state)
const performLocalSearch = (query: string) => {
  if (!query.trim()) {
    return [];
  }

  const searchTerm = query.toLowerCase().trim();
  const results: any[] = [];

  // Search in main services
  mainServices.forEach(service => {
    const matchesName = service.name.toLowerCase().includes(searchTerm);
    const matchesDescription = service.description.toLowerCase().includes(searchTerm);
    const matchesCategory = service.category.toLowerCase().includes(searchTerm);

    if (matchesName || matchesDescription || matchesCategory) {
      results.push({
        type: 'service',
        id: service.id,
        name: service.name,
        description: service.description,
        image: service.image,
        priceRange: service.priceRange,
        category: service.category
      });
    }
  });

  // Search in sub-services
  Object.entries(subServicesData).forEach(([serviceId, subServices]) => {
    subServices.forEach(subService => {
      const matchesName = subService.name.toLowerCase().includes(searchTerm);
      const matchesDescription = subService.description.toLowerCase().includes(searchTerm);
      
      // Search in additional fields if they exist
      const matchesMaterials = subService.materials?.some(material => 
        material.toLowerCase().includes(searchTerm)
      ) || false;
      
      const matchesIncludes = subService.includes?.some(include => 
        include.toLowerCase().includes(searchTerm)
      ) || false;
      
      const matchesBestFor = subService.bestFor?.some(purpose => 
        purpose.toLowerCase().includes(searchTerm)
      ) || false;
      
      const matchesLanguages = subService.languages?.some(language => 
        language.toLowerCase().includes(searchTerm)
      ) || false;

      // Search for common spiritual terms
      const spiritualTerms = [
        'puja', 'pujan', 'worship', 'prayer', 'mantra', 'chanting',
        'blessing', 'blessings', 'protection', 'spiritual', 'ceremony', 'ritual',
        'shiva', 'lord', 'god', 'divine', 'sacred', 'holy', 'temple',
        'abhishek', 'abhishekh', 'havan', 'yajna', 'aarti', 'arti',
        'priest', 'pandit', 'brahmin', 'vedic', 'hindu', 'hinduism',
        'ganesh', 'ganesha', 'hanuman', 'vishnu', 'lakshmi', 'durga',
        'rudra', 'mrityunjaya', 'maha', 'chalisa', 'path', 'sahasranama'
      ];
      const matchesSpiritual = spiritualTerms.some(term => {
        const termMatch = searchTerm.includes(term.toLowerCase()) || term.toLowerCase().includes(searchTerm);
        return termMatch && (matchesName || matchesDescription);
      });

      // Search in ritual steps
      const matchesRitualSteps = subService.ritualSteps?.some(step => 
        step.toLowerCase().includes(searchTerm)
      ) || false;

      if (matchesName || matchesDescription || matchesMaterials || matchesIncludes || 
          matchesBestFor || matchesLanguages || matchesSpiritual || matchesRitualSteps) {
        results.push({
          type: 'subservice',
          id: subService.id,
          name: subService.name,
          description: subService.description,
          image: subService.image,
          price: subService.price,
          serviceId: serviceId,
          rating: subService.rating,
          duration: subService.duration,
          bookingUrl: subService.bookingUrl
        });
      }
    });
  });

  // Sort results by relevance
  results.sort((a, b) => {
    const aExactName = a.name.toLowerCase() === searchTerm;
    const bExactName = b.name.toLowerCase() === searchTerm;
    
    if (aExactName && !bExactName) return -1;
    if (!aExactName && bExactName) return 1;
    
    const aStartsWithName = a.name.toLowerCase().startsWith(searchTerm);
    const bStartsWithName = b.name.toLowerCase().startsWith(searchTerm);
    
    if (aStartsWithName && !bStartsWithName) return -1;
    if (!aStartsWithName && bStartsWithName) return 1;
    
    // Prioritize sub-services over main services for more specific results
    if (a.type === 'subservice' && b.type === 'service') return -1;
    if (a.type === 'service' && b.type === 'subservice') return 1;
    
    return a.name.localeCompare(b.name);
  });

  return results;
};

const Header: React.FC = () => {
  const [searchFocused, setSearchFocused] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [liveSearchResults, setLiveSearchResults] = useState<any[]>([]);
  // Services dropdown temporarily disabled
  // const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  // const servicesDropdownRef = useRef<HTMLLIElement>(null);
  
  const { user, isAuthenticated, login, logout, isLoading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isPitruJourney = location.pathname === '/pitru-paksha' || location.pathname.startsWith('/pitru-paksha/');
  
  // Check if current page is provider dashboard or related pages
  const isProviderPage = location.pathname.startsWith('/dashboard/provider') || 
                       location.pathname.startsWith('/provider/') ||
                       location.pathname.startsWith('/dashboard/admin');
  
  // Debug logging for provider page detection
  useEffect(() => {
    console.log('🔍 Header Provider Page Detection:', {
      pathname: location.pathname,
      isProviderPage,
      startsWithDashboardProvider: location.pathname.startsWith('/dashboard/provider'),
      startsWithProvider: location.pathname.startsWith('/provider/'),
      startsWithDashboardAdmin: location.pathname.startsWith('/dashboard/admin')
    });
  }, [location.pathname, isProviderPage]);  // Update cart and wishlist counts
  const updateCounts = () => {
    setCartCount(getCartItemCount());
    setWishlistCount(getWishlistItemCount());
  };

  useEffect(() => {
    // Initial count
    updateCounts();

    // Listen for cart and wishlist updates
    window.addEventListener('cartUpdated', updateCounts);
    window.addEventListener('wishlistUpdated', updateCounts);
    window.addEventListener('storage', updateCounts);

    // Close user menu when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.user-menu-container')) {
        setUserMenuOpen(false);
      }
      // Close search dropdown when clicking outside search container
      if (searchRef.current && !searchRef.current.contains(target)) {
        setSearchFocused(false);
      }
      // Close services dropdown when clicking outside - temporarily disabled
      // if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(target)) {
      //   setServicesDropdownOpen(false);
      // }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('cartUpdated', updateCounts);
      window.removeEventListener('wishlistUpdated', updateCounts);
      window.removeEventListener('storage', updateCounts);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Debug user object when it changes
  useEffect(() => {
    if (user) {
      console.log('=== HEADER USER DEBUG ===', {
        user: user,
        firstName: user.firstName,
        lastName: user.lastName,
        username: user.username,
        email: user.email
      });
    }
  }, [user]);

  const handleLogin = () => {
    login();
  };

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    setMobileMenuOpen(false); // Close mobile menu on logout
  };

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
    setMobileMenuOpen(false); // Close mobile menu when opening user menu
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInputValue(value);
    
    // Debounce search to avoid too many calls while typing
    debouncedSearch(value);
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value.trim()) {
        const results = performLocalSearch(value);
        setLiveSearchResults(results);
      } else {
        setLiveSearchResults([]);
      }
    }, 300),
    []
  );

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchInputValue.trim()) {
      setSearchFocused(false);
      navigate(`/search?q=${encodeURIComponent(searchInputValue.trim())}`);
    }
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearchSubmit(e as any);
    }
  };

  const handleSearchFocus = () => {
    setSearchFocused(true);
  };

  const handleSearchBlur = () => {
    // Don't close immediately - let click events handle this
    // The outside click handler will close it
  };

  const handleSearchResultClick = (result: any) => {
    // Navigate first, before clearing
    if (result.type === 'service') {
      navigate(`/service/${result.id}`);
    } else if (result.type === 'subservice') {
      navigate(`/service/${result.serviceId}`, { 
        state: { highlightSubService: result.id } 
      });
    }
    
    // Then clear the search and close dropdown
    setSearchInputValue('');
    setSearchFocused(false);
    setLiveSearchResults([]);
  };

  const handleClearSearch = () => {
    setSearchInputValue('');
    setSearchFocused(false);
    setLiveSearchResults([]);
  };

  return (
    <header className="header">
      <nav className="nav-container">
        {/* Logo/Branding */}
        <div className="logo">
          <Link to="/">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z"/>
            </svg>
            <span>Antaryatra</span>
          </Link>
        </div>

        {/* Navigation Menu */}
        <ul className={`nav-menu ${mobileMenuOpen ? 'nav-menu--open' : ''}`}>
          {!isProviderPage && (
            <li 
              className="services-dropdown-item"
              // Services dropdown functionality temporarily disabled
              // ref={servicesDropdownRef}
              // onMouseEnter={() => setServicesDropdownOpen(true)}
              // onMouseLeave={() => setServicesDropdownOpen(false)}
            >
              {/* Services menu is temporarily disabled */}
              {/* <Link to="/services" className="services-link">
                Services
                <svg 
                  className={`dropdown-arrow ${servicesDropdownOpen ? 'dropdown-arrow--open' : ''}`} 
                  width="12" 
                  height="12" 
                  viewBox="0 0 12 12" 
                  fill="currentColor"
                >
                  <path d="M6 8.5L2.5 5h7L6 8.5z"/>
                </svg>
              </Link> */}
            
            {/* Services Dropdown - Temporarily disabled
            <div className={`services-dropdown ${servicesDropdownOpen ? 'services-dropdown--open' : ''}`}>
              <div className="services-dropdown-content">
                <div className="service-categories">
                  {mainServices.map((service) => (
                    <div key={service.id} className="category-group">
                      <div className="main-category">
                        <Link 
                          to={`/service/${service.id}`}
                          className="category-item main-category-link"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            setServicesDropdownOpen(false);
                          }}
                        >
                          <div className="category-info">
                            <h4>{service.name}</h4>
                          </div>
                        </Link>
                        
                        {subServicesData[service.id] && (
                          <div className="sub-services-list">
                            {subServicesData[service.id].map((subService) => (
                              <Link
                                key={subService.id}
                                to={`/sub-service/${subService.id}`}
                                className="sub-service-item"
                                onClick={() => {
                                  setMobileMenuOpen(false);
                                  setServicesDropdownOpen(false);
                                }}
                              >
                                <span className="sub-service-name">{subService.name}</span>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  <div className="category-group custom-solutions">
                    <Link 
                      to="/contact"
                      className="category-item custom-category-link"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setServicesDropdownOpen(false);
                      }}
                    >
                      <div className="category-info">
                        <h4>Custom Spiritual Solutions</h4>
                        <span className="custom-badge">Contact Us</span>
                      </div>
                    </Link>
                  </div>
                </div>
                
                <div className="popular-services">
                  <h4>Popular Services</h4>
                  <Link to="/sub-service/1" onClick={() => {
                    setMobileMenuOpen(false);
                    setServicesDropdownOpen(false);
                  }}>Maha Mrityunjaya Pujan</Link>
                  <Link to="/sub-service/2" onClick={() => {
                    setMobileMenuOpen(false);
                    setServicesDropdownOpen(false);
                  }}>Rudrabhishek</Link>
                  <Link to="/sub-service/19" onClick={() => {
                    setMobileMenuOpen(false);
                    setServicesDropdownOpen(false);
                  }}>Grih Pravesh Pujan</Link>
                  <Link to="/sub-service/7" onClick={() => {
                    setMobileMenuOpen(false);
                    setServicesDropdownOpen(false);
                  }}>Shani Shanti Pujan</Link>
                </div>
                
                <div className="view-all-services">
                  <Link 
                    to="/services" 
                    className="view-all-btn"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setServicesDropdownOpen(false);
                    }}
                  >
                    View All Services →
                  </Link>
                </div>
              </div>
            </div>
            */}
            </li>
          )}
          <li><Link to="/about" onClick={() => setMobileMenuOpen(false)}>About</Link></li>
          <li><Link to="/contact" onClick={() => setMobileMenuOpen(false)}>Contact</Link></li>
        </ul>

        {/* Search Bar - Hidden on provider pages and the consultation-led landing page */}
        {!isProviderPage && !isPitruJourney && (
          <div 
            className={`search-container ${searchFocused ? 'search-container--focused' : ''}`}
            ref={searchRef}
          >
            <form onSubmit={handleSearchSubmit} className="search-form">
              <div className="search-wrapper">
                <svg className="search-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path fillRule="evenodd" d="M11.5 7a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0zm-.82 4.74a6 6 0 1 1 1.06-1.06l3.04 3.04a.75.75 0 1 1-1.06 1.06l-3.04-3.04z"/>
                </svg>
                <input
                  type="text"
                  placeholder="Search pujas and services..."
                  className="search-input"
                  value={searchInputValue}
                  onChange={handleSearchInputChange}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  onKeyPress={handleSearchKeyPress}
                />
                {searchInputValue && (
                  <button
                    type="button"
                    className="search-clear"
                    onClick={handleClearSearch}
                  >
                    ✕
                  </button>
                )}
              </div>
            </form>
            
            {/* Search Results Dropdown */}
            {searchFocused && liveSearchResults.length > 0 && (
              <div 
                className="search-dropdown"
                onMouseDown={(e) => e.preventDefault()} // Prevent blur when clicking dropdown
              >
                <div className="search-results-preview">
                  {liveSearchResults.slice(0, 5).map((result: any) => (
                    <button
                      key={`${result.type}-${result.id}`}
                      className="search-result-item"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSearchResultClick(result);
                      }}
                    >
                      <img src={result.image} alt={result.name} className="result-thumbnail" />
                      <div className="result-info">
                        <div className="result-name">{result.name}</div>
                        <div className="result-type">
                          {result.type === 'service' ? 'Service Category' : 'Puja Service'}
                          {result.price && <span className="result-price">{result.price}</span>}
                        </div>
                      </div>
                    </button>
                  ))}
                  {liveSearchResults.length > 5 && (
                    <div 
                      className="search-see-all"
                      onMouseDown={(e) => {
                        e.preventDefault(); // Prevent input blur
                        setSearchFocused(false);
                        navigate(`/search?q=${encodeURIComponent(searchInputValue.trim())}`);
                      }}
                    >
                      See all {liveSearchResults.length} results →
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Login/User Section */}
        {!isPitruJourney && <div className="auth-section">
          {/* Cart and Wishlist Icons - Hidden on provider pages */}
          {!isProviderPage && (
            <div className="header-icons">
              <Link to="/cart" className="icon-link">
                <div className="icon-container">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 18C5.9 18 5.01 18.9 5.01 20S5.9 22 7 22 8.99 21.1 8.99 20 8.1 18 7 18ZM1 2V4H3L6.6 11.59L5.24 14.04C5.09 14.32 5 14.65 5 15C5 16.1 5.9 17 7 17H19V15H7.42C7.28 15 7.17 14.89 7.17 14.75L7.2 14.63L8.1 13H15.55C16.3 13 16.96 12.58 17.3 11.97L20.88 5H5.21L4.27 3H1V2H1ZM17 18C15.9 18 15.01 18.9 15.01 20S15.9 22 17 22 18.99 21.1 18.99 20 18.1 18 17 18Z" fill="currentColor"/>
                  </svg>
                  {cartCount > 0 && <span className="icon-badge">{cartCount}</span>}
                </div>
              </Link>
              
              <Link to="/wishlist" className="icon-link">
                <div className="icon-container">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.04L12 21.35Z" fill="currentColor"/>
                  </svg>
                  {wishlistCount > 0 && <span className="icon-badge">{wishlistCount}</span>}
                </div>
              </Link>
            </div>
          )}

          {/* Language Switcher hidden until Hindi translation is complete */}
          {/* <div className="header-icons">
            <LanguageSwitcher />
          </div> */}

          {/* Authentication UI */}
          {isLoading ? (
            <div className="auth-loading">
              <div className="spinner"></div>
            </div>
          ) : isAuthenticated && user ? (
            <div className="user-menu-container">
              <button 
                className="user-button"
                onClick={toggleUserMenu}
                aria-label="User menu"
              >
                <div className="user-avatar">
                  {user.firstName ? user.firstName.charAt(0).toUpperCase() : user.username.charAt(0).toUpperCase()}
                </div>
                <span className="user-name">
                  {user.firstName && user.lastName 
                    ? `${user.firstName} ${user.lastName}` 
                    : user.firstName || user.username}
                </span>
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="currentColor"
                  className={`dropdown-arrow ${userMenuOpen ? 'dropdown-arrow--open' : ''}`}
                >
                  <path d="M7 10l5 5 5-5z"/>
                </svg>
              </button>
              
              {userMenuOpen && (
                <div className="user-dropdown">
                  <div className="user-info">
                    <div className="user-details">
                      <div className="user-display-name">
                        {user.firstName && user.lastName 
                          ? `${user.firstName} ${user.lastName}` 
                          : user.username}
                      </div>
                      <div className="user-email">{user.username}</div>
                    </div>
                  </div>
                  <div className="dropdown-divider"></div>
                  {/* Profile and Orders - Hidden on provider pages */}
                  {!isProviderPage && (
                    <>
                      <Link to="/profile" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                        </svg>
                        Profile
                      </Link>
                      <Link to="/orders" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                        </svg>
                        Orders
                      </Link>
                    </>
                  )}
                  {isAdmin && isAdmin() && (
                    <Link to="/dashboard/admin" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.21,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
                      </svg>
                      Admin Dashboard
                    </Link>
                  )}
                  <div className="dropdown-divider"></div>
                  <button className="dropdown-item logout-item" onClick={handleLogout}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
                    </svg>
                    Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className="login-btn" onClick={handleLogin}>
              Login
            </button>
          )}
        </div>}

        {/* Mobile Menu Toggle */}
        <button 
          className={`mobile-menu-btn ${mobileMenuOpen ? 'mobile-menu-btn--open' : ''}`}
          onClick={() => {
            setMobileMenuOpen(!mobileMenuOpen);
            setUserMenuOpen(false); // Close user menu when opening mobile menu
          }}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </nav>
    </header>
  );
};

export default Header;